/**
 * UploadSessionContext — estado de la cola de "Cargar Archivos" y de los resultados de la subida.
 *
 * Vive por encima de <Routes> (App.tsx), así que no se desmonta al cambiar de pantalla: el
 * usuario puede ir a otra ruta y volver a /upload sin perder la lista.
 *
 *   - Solo memoria (React state). NO se guarda nada en localStorage/sessionStorage: son datos
 *     financieros (comprobantes, asientos). Si se recarga el navegador la lista se pierde, pero
 *     lo ya guardado en la base de datos sigue ahí (Upload vuelve a cargar los borradores).
 *   - La lista solo se quita cuando el usuario la limpia (clearSession) o cambia de empresa
 *     activa — el sync procesa los archivos con la empresa activa, así que una cola de otra
 *     empresa nunca debe llegar a sincronizarse.
 *
 * Los setters son los de useState (admiten `prev => …`) para que Upload.tsx los use igual que antes.
 */

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type Dispatch, type MutableRefObject, type ReactNode, type SetStateAction,
} from 'react';
import { useCompanyContext } from './CompanyContext';
import type { ProcessedFile, SyncResult } from '../pages/Upload';

export type SessionMode = 'compra' | 'venta' | 'auto';
export type SyncStatus = 'idle' | 'syncing' | 'done' | 'error';
export interface BatchProgress { current: number; total: number }
export interface BatchSummary { processed: number; errors: { name: string; msg: string }[] }

export interface UploadSession {
  files: ProcessedFile[];
  setFiles: Dispatch<SetStateAction<ProcessedFile[]>>;
  sessionMode: SessionMode;
  setSessionMode: Dispatch<SetStateAction<SessionMode>>;
  pdfMap: Map<string, File>;
  setPdfMap: Dispatch<SetStateAction<Map<string, File>>>;
  bankPdfMap: Map<string, File>;
  setBankPdfMap: Dispatch<SetStateAction<Map<string, File>>>;
  syncStatus: SyncStatus;
  setSyncStatus: Dispatch<SetStateAction<SyncStatus>>;
  syncErrorMsg: string | null;
  setSyncErrorMsg: Dispatch<SetStateAction<string | null>>;
  syncResults: SyncResult[];
  setSyncResults: Dispatch<SetStateAction<SyncResult[]>>;
  detectedPeriod: string | null;
  setDetectedPeriod: Dispatch<SetStateAction<string | null>>;
  batchProgress: BatchProgress | null;
  setBatchProgress: Dispatch<SetStateAction<BatchProgress | null>>;
  batchSummary: BatchSummary | null;
  setBatchSummary: Dispatch<SetStateAction<BatchSummary | null>>;
  periodoInicio: string;
  setPeriodoInicio: Dispatch<SetStateAction<string>>;
  periodoFin: string;
  setPeriodoFin: Dispatch<SetStateAction<string>>;
  /** docId de los PDF ya subidos: un reintento del sync no los sube de nuevo (sin duplicados). */
  pdfDocCacheRef: MutableRefObject<Map<string, number>>;
  /** Vacía la cola y todos los resultados. Es la única forma de quitar la lista (además de cambiar de empresa). */
  clearSession: () => void;
}

const UploadSessionContext = createContext<UploadSession | null>(null);

export function UploadSessionProvider({ children }: { children: ReactNode }) {
  const { activeCompany } = useCompanyContext();
  const companyId = activeCompany?.companyId ?? null;

  const [files,          setFiles]          = useState<ProcessedFile[]>([]);
  const [sessionMode,    setSessionMode]    = useState<SessionMode>('auto');
  const [pdfMap,         setPdfMap]         = useState<Map<string, File>>(new Map());
  const [bankPdfMap,     setBankPdfMap]     = useState<Map<string, File>>(new Map());
  const [syncStatus,     setSyncStatus]     = useState<SyncStatus>('idle');
  const [syncErrorMsg,   setSyncErrorMsg]   = useState<string | null>(null);
  const [syncResults,    setSyncResults]    = useState<SyncResult[]>([]);
  const [detectedPeriod, setDetectedPeriod] = useState<string | null>(null);
  const [batchProgress,  setBatchProgress]  = useState<BatchProgress | null>(null);
  const [batchSummary,   setBatchSummary]   = useState<BatchSummary | null>(null);
  const [periodoInicio,  setPeriodoInicio]  = useState('');
  const [periodoFin,     setPeriodoFin]     = useState('');
  const pdfDocCacheRef = useRef<Map<string, number>>(new Map());

  const resetState = useCallback(() => {
    setFiles([]);
    setSessionMode('auto');
    setPdfMap(new Map());
    setBankPdfMap(new Map());
    setSyncStatus('idle');
    setSyncErrorMsg(null);
    setSyncResults([]);
    setDetectedPeriod(null);
    setBatchProgress(null);
    setBatchSummary(null);
    setPeriodoInicio('');
    setPeriodoFin('');
  }, []);

  const clearSession = useCallback(() => {
    resetState();
    pdfDocCacheRef.current.clear();
  }, [resetState]);

  // Cambio de empresa activa → la cola de la anterior no se conserva. Se ajusta durante el render
  // (patrón de React para "reiniciar estado cuando cambia una prop"): sin un render intermedio
  // con la lista de la otra empresa.
  const [ownerCompanyId, setOwnerCompanyId] = useState(companyId);
  if (ownerCompanyId !== companyId) {
    setOwnerCompanyId(companyId);
    resetState();
  }
  useEffect(() => { pdfDocCacheRef.current.clear(); }, [companyId]);

  return (
    <UploadSessionContext.Provider value={{
      files, setFiles,
      sessionMode, setSessionMode,
      pdfMap, setPdfMap,
      bankPdfMap, setBankPdfMap,
      syncStatus, setSyncStatus,
      syncErrorMsg, setSyncErrorMsg,
      syncResults, setSyncResults,
      detectedPeriod, setDetectedPeriod,
      batchProgress, setBatchProgress,
      batchSummary, setBatchSummary,
      periodoInicio, setPeriodoInicio,
      periodoFin, setPeriodoFin,
      pdfDocCacheRef,
      clearSession,
    }}>
      {children}
    </UploadSessionContext.Provider>
  );
}

export function useUploadSession(): UploadSession {
  const ctx = useContext(UploadSessionContext);
  if (!ctx) throw new Error('useUploadSession must be used within <UploadSessionProvider>');
  return ctx;
}
